import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const DAYS_OF_WEEK = [
  { idx: 0, label: "Mon" },
  { idx: 1, label: "Tue" },
  { idx: 2, label: "Wed" },
  { idx: 3, label: "Thu" },
  { idx: 4, label: "Fri" },
  { idx: 5, label: "Sat" },
  { idx: 6, label: "Sun" },
];

export default function WorkingDaysSection({ settings, refresh }) {
  const defaults = () => ({
    weekly_off: settings.weekly_off || [5, 6],
    office_hours_start: settings.office_hours_start || "09:30",
    office_hours_end: settings.office_hours_end || "18:30",
    late_threshold_minutes: settings.late_threshold_minutes ?? 15,
    full_day_min_hours: settings.full_day_min_hours ?? 7.0,
    half_day_min_hours: settings.half_day_min_hours ?? 3.5,
    geofence_enabled: !!settings.geofence_enabled,
    office_lat: settings.office_lat ?? "",
    office_lng: settings.office_lng ?? "",
    office_radius_km: settings.office_radius_km ?? 1.0,
  });
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(defaults()); /* eslint-disable-next-line */ }, [settings]);

  const toggleDay = (idx) => {
    const next = form.weekly_off.includes(idx)
      ? form.weekly_off.filter((d) => d !== idx)
      : [...form.weekly_off, idx].sort();
    setForm({ ...form, weekly_off: next });
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          office_lat: Number(pos.coords.latitude.toFixed(6)),
          office_lng: Number(pos.coords.longitude.toFixed(6)),
        }));
        toast.success("Captured current location");
      },
      (err) => toast.error(`Failed: ${err.message}`),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        weekly_off: form.weekly_off,
        office_hours_start: form.office_hours_start,
        office_hours_end: form.office_hours_end,
        late_threshold_minutes: Number(form.late_threshold_minutes) || 0,
        full_day_min_hours: Number(form.full_day_min_hours) || 7.0,
        half_day_min_hours: Number(form.half_day_min_hours) || 3.5,
        geofence_enabled: form.geofence_enabled,
        office_lat: form.office_lat === "" ? null : Number(form.office_lat),
        office_lng: form.office_lng === "" ? null : Number(form.office_lng),
        office_radius_km: Number(form.office_radius_km) || 1.0,
      };
      await api.patch("/settings", payload);
      toast.success("Working days saved");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setSaving(false);
  };

  return (
    <section data-testid="section-working-days">
      <h2 className="text-lg font-semibold tracking-tight mb-1" style={{ fontFamily: "Outfit" }}>Working Days & Office Hours</h2>
      <p className="text-xs text-slate-500 mb-5">
        Drives weekly-off marking in attendance, the "Late by Xm" tag on check-ins, and the optional geofence around your office.
      </p>

      <div className="border border-slate-200 p-5 rounded-sm space-y-6">
        {/* Weekly off picker */}
        <div>
          <Label className="text-xs">Weekly Off Days</Label>
          <div className="mt-2 flex gap-1.5 flex-wrap" data-testid="weekly-off-picker">
            {DAYS_OF_WEEK.map((d) => {
              const on = form.weekly_off.includes(d.idx);
              return (
                <button
                  key={d.idx}
                  type="button"
                  onClick={() => toggleDay(d.idx)}
                  className={`px-3 py-1.5 rounded-sm text-xs border transition-colors ${
                    on
                      ? "bg-[#0a2540] text-white border-[#0a2540]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#0a2540]"
                  }`}
                  data-testid={`weekday-${d.label.toLowerCase()}`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          <div className="mt-1.5 text-[10px] text-slate-400">
            Click to toggle. Days marked as <strong>Weekly Off</strong> count as paid days in attendance.
          </div>
        </div>

        {/* Office hours */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Office hours start</Label>
            <Input type="time" value={form.office_hours_start}
                   onChange={(e) => setForm({ ...form, office_hours_start: e.target.value })}
                   className="mt-1 rounded-sm font-mono-data"
                   data-testid="input-office-start" />
          </div>
          <div>
            <Label className="text-xs">Office hours end</Label>
            <Input type="time" value={form.office_hours_end}
                   onChange={(e) => setForm({ ...form, office_hours_end: e.target.value })}
                   className="mt-1 rounded-sm font-mono-data"
                   data-testid="input-office-end" />
          </div>
          <div>
            <Label className="text-xs">Late grace (minutes)</Label>
            <Input type="number" min="0" max="240" value={form.late_threshold_minutes}
                   onChange={(e) => setForm({ ...form, late_threshold_minutes: e.target.value })}
                   className="mt-1 rounded-sm font-mono-data"
                   data-testid="input-late-grace" />
          </div>
        </div>

        {/* Attendance crediting rules */}
        <div className="border-t border-slate-200 pt-5" data-testid="attendance-thresholds">
          <Label className="text-xs">Attendance Crediting</Label>
          <p className="mt-1 text-[11px] text-slate-500 max-w-2xl">
            How many hours worked (check-in → check-out) qualifies an employee for a Full Day or Half Day. Defaults: ≥ 7.0 h → Full Day, 3.5–7.0 h → Half Day, &lt; 3.5 h → Short Day. Half-day leave + worked half still combines to a paid day.
          </p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
            <div>
              <Label className="text-xs">Full day minimum (hours)</Label>
              <Input
                type="number" min="1" max="24" step="0.25"
                value={form.full_day_min_hours}
                onChange={(e) => setForm({ ...form, full_day_min_hours: e.target.value })}
                className="mt-1 rounded-sm font-mono-data"
                data-testid="input-full-day-hours"
              />
              <div className="mt-1 text-[10px] text-slate-400">
                ≥ this many hours worked → credited as <span className="font-semibold">Present (+1.0)</span>.
              </div>
            </div>
            <div>
              <Label className="text-xs">Half day minimum (hours)</Label>
              <Input
                type="number" min="0.5" max="12" step="0.25"
                value={form.half_day_min_hours}
                onChange={(e) => setForm({ ...form, half_day_min_hours: e.target.value })}
                className="mt-1 rounded-sm font-mono-data"
                data-testid="input-half-day-hours"
              />
              <div className="mt-1 text-[10px] text-slate-400">
                ≥ this many hours but &lt; Full → <span className="font-semibold">Half Day (+0.5)</span>. Below this → <span className="font-semibold">Short Day</span>.
              </div>
            </div>
          </div>
        </div>

        {/* Geofence */}
        <div className="border-t border-slate-200 pt-5">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.geofence_enabled}
                   onChange={(e) => setForm({ ...form, geofence_enabled: e.target.checked })}
                   data-testid="checkbox-geofence" />
            <span className="text-sm font-medium">Enable office geofence (tag remote check-ins)</span>
          </label>
          <div className="mt-1 pl-6 text-[10px] text-slate-400 max-w-lg">
            When enabled, check-ins outside the radius are still allowed but tagged "Remote" — useful for spotting work-from-home or field check-ins at a glance.
          </div>
          {form.geofence_enabled && (
            <div className="mt-3 pl-6 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Latitude</Label>
                <Input value={form.office_lat} onChange={(e) => setForm({ ...form, office_lat: e.target.value })}
                       placeholder="28.6139" className="mt-1 rounded-sm font-mono-data" data-testid="input-office-lat" />
              </div>
              <div>
                <Label className="text-xs">Longitude</Label>
                <Input value={form.office_lng} onChange={(e) => setForm({ ...form, office_lng: e.target.value })}
                       placeholder="77.2090" className="mt-1 rounded-sm font-mono-data" data-testid="input-office-lng" />
              </div>
              <div>
                <Label className="text-xs">Radius (km)</Label>
                <Input type="number" step="0.1" min="0.05" value={form.office_radius_km}
                       onChange={(e) => setForm({ ...form, office_radius_km: e.target.value })}
                       className="mt-1 rounded-sm font-mono-data" data-testid="input-office-radius" />
              </div>
              <div className="self-end">
                <Button variant="outline" className="rounded-sm w-full" onClick={useCurrentLocation} data-testid="btn-use-location">
                  Use my location
                </Button>
              </div>
            </div>
          )}
        </div>

        <div>
          <Button onClick={save} disabled={saving}
                  className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm"
                  data-testid="btn-save-working-days">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </section>
  );
}
